/**
 * Shared helpers for editing a feature sheet as an ordered list of key/value
 * rows. Used by both the Parts AI run feature sheet (PartsAIFeatureSheet) and
 * the part Edit modal (EditPartModal) via the FeatureRowsEditor component.
 *
 * A `__rowId` gives each row a stable identity independent of its
 * feature/value, so the UI can reorder, highlight, and delete rows without
 * relying on the (mutable, possibly-duplicate) feature key.
 */

export type FeatureSheetRow = {
  feature: string;
  value: string;
  __rowId: number;
};

let rowIdCounter = 0;

export const createEmptyRow = (): FeatureSheetRow => ({
  feature: "",
  value: "",
  __rowId: rowIdCounter++,
});

/**
 * Convert a features record from the API into ordered, presentable rows.
 * Uses `featureOrder` to preserve the backend's display order, falling back to
 * object key order. An empty/absent sheet yields a single blank row to edit.
 */
export const featuresToRows = (
  features: Record<string, string> | undefined,
  featureOrder?: string[],
): FeatureSheetRow[] => {
  if (!features || Object.keys(features).length === 0) {
    return [createEmptyRow()];
  }

  const orderedKeys = featureOrder ?? Object.keys(features);

  return orderedKeys
    .filter((feature) => feature in features) // only keys that still exist
    .map((feature) => ({
      feature,
      value: features[feature],
      __rowId: rowIdCounter++,
    }));
};

/**
 * Convert edited rows back into a features record for the API. Rows are kept
 * in order (the object's insertion order is the feature order the backend
 * derives) and blank-keyed rows are dropped. A later row with a duplicate key
 * overwrites an earlier one.
 */
export const rowsToFeatures = (
  rows: FeatureSheetRow[],
): Record<string, string> => {
  const features: Record<string, string> = {};

  rows.forEach((row) => {
    const feature = row.feature.trim();
    if (feature) {
      features[feature] = row.value.trim();
    }
  });

  return features;
};
